/**
 * Utility functions shared across the application
 */

/**
 * Debounce function that delays the execution of a function until after a specified wait time
 * @param {Function} fn - The function to debounce
 * @param {number} wait - The number of milliseconds to delay
 * @returns {Function} The debounced function
 */
export function debounce(fn, wait) {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), wait);
  };
}

/** Breathing room left above a list when it is scrolled back into view, in px. */
const LIST_REVEAL_GAP = 24;

/**
 * Re-orients the reader after a "Show less" collapse.
 *
 * Collapsing pulls content out from above the fold, so the page shrinks underneath the
 * reader and the browser strands them past the end of the now-short list. The two
 * obvious responses are both wrong: jumping to the top of the whole section reads as a
 * page reset, and holding the exact scroll position leaves them staring at whitespace
 * below the list. Returning to the top of the list itself is the middle ground — the
 * first items are back on screen, so it's clear what the list collapsed back to, and the
 * surrounding page is not lost.
 *
 * Does nothing when the list start is already on screen, and never scrolls downwards, so
 * collapsing can only ever move the reader up or leave them where they are. The sticky
 * header is accounted for so the first row doesn't end up hidden underneath it.
 *
 * @param {HTMLElement} [list] - Container holding the list items.
 * @returns {void}
 */
export function revealListStart(list) {
  if (!list) {
    return;
  }

  // Collapsing shortens the page, and the browser clamps the scroll position itself once
  // it has relaid out. Measuring on the next frame — and in absolute document
  // coordinates rather than as a relative scrollBy — keeps this from racing that clamp
  // and landing short. The jump is instant because the content it was scrolled past has
  // already gone; animating across thousands of pixels of vanished list is just slow.
  requestAnimationFrame(() => {
    const header = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 0;
    const top = list.getBoundingClientRect().top + window.scrollY - header - LIST_REVEAL_GAP;

    if (window.scrollY <= top) {
      return;
    }

    window.scrollTo({ top: Math.max(top, 0), left: 0, behavior: 'auto' });
  });
}

/** `icon-caret.svg` inlined verbatim — see `renderPagination` for why this can't be an asset fetch. */
const PAGINATION_CARET_SVG =
  '<svg class="icon icon-caret" viewBox="0 0 10 6"><path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"/></svg>';

/**
 * Renders numbered pagination into `container`, in the theme's existing `.pagination`
 * component markup (see `snippets/component-pagination.liquid`, used on Collection,
 * Blog, Search) — so a client-side-paged list (reviews, Learn More articles: every item
 * is already rendered, nothing to fetch) looks identical to a server-paginated one.
 *
 * @param {HTMLElement} container - Emptied and (re)filled with the `.pagination` markup.
 * @param {Object} state
 * @param {number} state.currentPage - 1-based.
 * @param {number} state.totalPages
 * @param {(page: number) => void} state.onChange - Called with the requested page number.
 * @returns {void}
 */
export function renderPagination(container, { currentPage, totalPages, onChange }) {
  if (!container) {
    return;
  }

  container.hidden = totalPages <= 1;
  container.innerHTML = '';
  if (container.hidden) {
    return;
  }

  const list = document.createElement('ul');
  list.className = 'pagination__list';

  const addItem = (label, { page, current = false, direction, disabled = false, ariaLabel } = {}) => {
    const li = document.createElement('li');
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `pagination__item${current ? ' pagination__item--current' : ''}${
      direction ? ` pagination__item-arrow pagination__item--${direction}` : ''
    }`;
    button.innerHTML = label;
    if (ariaLabel) {
      button.setAttribute('aria-label', ariaLabel);
    }
    if (current) {
      button.setAttribute('aria-current', 'page');
    }

    if (disabled || current) {
      button.disabled = disabled;
      if (current) {
        button.setAttribute('aria-disabled', 'true');
      }
    } else {
      button.addEventListener('click', () => onChange(page));
    }

    li.appendChild(button);
    list.appendChild(li);
  };

  const arrowLabel = `<span class="svg-wrapper">${PAGINATION_CARET_SVG}</span>`;

  addItem(arrowLabel, { page: currentPage - 1, direction: 'previous', disabled: currentPage <= 1, ariaLabel: 'Previous page' });

  for (let page = 1; page <= totalPages; page += 1) {
    addItem(String(page), { page, current: page === currentPage, ariaLabel: `Page ${page}` });
  }

  addItem(arrowLabel, { page: currentPage + 1, direction: 'next', disabled: currentPage >= totalPages, ariaLabel: 'Next page' });

  container.appendChild(list);
}

/* Published as a global on purpose — section scripts must NOT `import` it.
 *
 * Shopify serves theme assets from versioned URLs (`theme.js?v=<hash>`), and that
 * version is the cache buster. A relative `import ... from './theme.js'` drops the
 * query string, so the browser fetches the *unversioned* URL and gets whatever the CDN
 * has cached — which can be an old copy that predates whatever you just added. A failed
 * named import is fatal and silent: the entire importing module is discarded, so its
 * `customElements.define` never runs and the component simply stops working. That is
 * exactly what took out "Load More Articles" and "Load More Reviews".
 *
 * Reading a global at click time has no such coupling: theme.liquid always loads this
 * file with its version, and by the time anyone clicks, it has run. */
window.revealListStart = revealListStart;
window.renderPagination = renderPagination;
